// Recipient cross-contact mismatch detection — mirrors
// backend/routes/quotes.py `_detect_recipient_mismatch` EXACTLY (same
// set-based algorithm; keep both in sync if either changes).
//
// A client has three independent contact slots (PM / Billing / Primary),
// each with its own name AND email. Two slots can share the exact same
// displayed name (e.g. Billing and Primary both "Theresa Munoz Wynn")
// while having different email addresses — a first-match-wins lookup
// would misclassify a perfectly valid pairing as a mismatch. This module
// instead compares full SETS of matching roles:
//
//   nameRoles  = every role whose name  == the stored recipient name
//   emailRoles = every role whose email == the stored billing_email
//
//   - Either set empty        -> a legitimate custom name/email; never flag.
//   - Sets intersect          -> at least one role explains BOTH fields; consistent.
//   - Both non-empty, disjoint -> genuine cross-contact mismatch.
//
// Extracted as a standalone module (rather than inlined in
// QuoteDetail.js) so it has real, importable, function-level tests
// instead of only source-text pattern assertions.

const ROLE_DEFS = [
  { role: "pm", source: "PM",
    first: (c) => c.pm_first_name, last: (c) => c.pm_last_name, email: (c) => c.pm_contact_email },
  { role: "billing", source: "Billing",
    first: (c) => c.billing_first_name, last: (c) => c.billing_last_name, email: (c) => c.billing_email },
  { role: "primary", source: "Primary contact",
    first: (c) => c.contact_first_name, last: (c) => c.contact_last_name, email: (c) => c.contact_email },
];

const norm = (s) => (s || "").trim().toLowerCase();

export const joinName = (first, last) =>
  [first, last].map((s) => (s || "").trim()).filter(Boolean).join(" ");

// Every PM/Billing/Primary contact slot on a client record, as
// {role, source, name, email}. Exported for the "Greet as" chip UI so
// the chip list and the mismatch detector always agree on what a role's
// name/email actually is.
export function clientContactSlots(client) {
  const c = client || {};
  return ROLE_DEFS.map((def) => ({
    role: def.role,
    source: def.source,
    name: joinName(def.first(c), def.last(c)),
    email: (def.email(c) || "").trim(),
  }));
}

// Returns null when the stored recipient pair is consistent (or there's
// nothing known to compare — a custom name/email is never flagged).
// Otherwise returns:
//   {
//     greetedName, greetedSources: string[], actualSources: string[],
//     actualEmail, ambiguous: boolean,
//     fixRole, fixEmail,   // null unless exactly one role explains the
//                          // greeted name — only then is a one-click
//                          // auto-repair safe to offer.
//   }
export function detectRecipientMismatch(quote, client) {
  const q = quote || {};
  const slots = clientContactSlots(client);
  const savedName = joinName(q.recipient_first_name, q.recipient_last_name);
  const savedEmail = (q.billing_email || "").trim();
  if (!savedName || !savedEmail) return null;

  const nameRoles = slots.filter((s) => s.name && norm(s.name) === norm(savedName));
  const emailRoles = slots.filter((s) => s.email && norm(s.email) === norm(savedEmail));
  if (!nameRoles.length || !emailRoles.length) return null;

  const nameRoleKeys = new Set(nameRoles.map((s) => s.role));
  if (emailRoles.some((s) => nameRoleKeys.has(s.role))) return null; // consistent

  const ambiguous = nameRoles.length > 1;
  return {
    greetedName: savedName,
    greetedSources: nameRoles.map((s) => s.source),
    actualSources: emailRoles.map((s) => s.source),
    actualEmail: emailRoles[0].email,
    ambiguous,
    fixRole: ambiguous ? null : nameRoles[0].role,
    fixEmail: ambiguous ? null : nameRoles[0].email,
  };
}
